import { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  Search, 
  Calendar,
  CheckCircle,
  Clock,
  AlertCircle,
  Grid3x3,
  Beaker,
  Dna,
  TestTube,
  FileText,
  ArrowRight
} from 'lucide-react';
import { listJobs } from '../lib/jobs';
import type { JobWithResult } from '../lib/jobs';
import { getProject, listProjects } from '../../projects/lib/projects';
import type { Project } from '../../../shared/lib/supabase';
import { useTabNavigation } from '../../../shared/hooks/useTabNavigation';

type StatusFilter = 'all' | 'completed' | 'running' | 'failed' | 'queued';
type WorkflowFilter = 'all' | 'enzyme' | 'antibody' | 'peptide';

interface JobGroup {
  baseJobId: string;
  baseJobName: string;
  generations: JobWithResult[];
}

export function JobsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isInTabContext, openGenerationTab } = useTabNavigation();
  const [jobs, setJobs] = useState<JobWithResult[]>([]);
  const [filteredJobs, setFilteredJobs] = useState<JobWithResult[]>([]);
  const [jobGroups, setJobGroups] = useState<JobGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [workflowFilter, setWorkflowFilter] = useState<WorkflowFilter>('all');
  const [currentProject, setCurrentProject] = useState<Project | null>(null);
  const [projectFilterActive, setProjectFilterActive] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);
  
  // Stats
  const [totalGenerations, setTotalGenerations] = useState(0);
  const [latestJobDate, setLatestJobDate] = useState<string | null>(null);
  const [activeStatus, setActiveStatus] = useState<string>('No active jobs');
  
  // Check if we're in a project context
  const storedProjectId = sessionStorage.getItem('currentProjectId');
  
  // Extract project ID from URL if present (e.g., /projects/{projectId}/jobs)
  const pathMatch = location.pathname.match(/\/projects\/([^\/]+)\/jobs/);
  const urlProjectId = pathMatch ? pathMatch[1] : null;
  const projectId = urlProjectId || storedProjectId;

  useEffect(() => {
    loadJobsAndProject();
  }, [projectId]);

  useEffect(() => {
    filterJobs();
    calculateStats();
  }, [jobs, searchQuery, statusFilter, workflowFilter, projectFilterActive]);

  useEffect(() => {
    groupJobs();
  }, [filteredJobs]);

  const loadJobsAndProject = async () => {
    try {
      const [jobsList, projectsList] = await Promise.all([
        listJobs(),
        listProjects()
      ]);
      setJobs(jobsList);
      setProjects(projectsList);
      
      // Check if we should filter by project
      if (projectId) {
        try {
          const project = await getProject(projectId);
          setCurrentProject(project);
          setProjectFilterActive(true);
          // Filter jobs immediately for this project
          const projectJobs = jobsList.filter(job => job.project_id === project.id);
          setFilteredJobs(projectJobs);
        } catch (error) {
          console.error('Error loading project:', error);
          // If project not found, show all jobs
          if (!urlProjectId) {
            sessionStorage.removeItem('currentProjectId');
          }
          setFilteredJobs(jobsList);
        }
      } else {
        // No project context, show all jobs
        setFilteredJobs(jobsList);
      }
    } catch (error) {
      console.error('Error loading jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterJobs = () => {
    let filtered = [...jobs];

    // Project filter
    if (projectFilterActive && currentProject) {
      filtered = filtered.filter(job => job.project_id === currentProject.id);
    }

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(job => 
        job.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        job.smiles.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(job => job.status === statusFilter);
    }

    // Workflow filter
    if (workflowFilter !== 'all') {
      filtered = filtered.filter(job => {
        const name = job.name.toLowerCase();
        return name.includes(workflowFilter);
      });
    }

    setFilteredJobs(filtered);
  };

  const calculateStats = () => {
    // Calculate total generations (max generation across all job families)
    const maxGeneration = filteredJobs.reduce((max, job) => Math.max(max, job.generation || 1), 0);
    setTotalGenerations(maxGeneration);

    // Find latest job date
    if (filteredJobs.length > 0) {
      const sortedJobs = [...filteredJobs].sort((a, b) => 
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
      setLatestJobDate(sortedJobs[0].created_at);
      
      // Determine active status
      const activeJobs = filteredJobs.filter(job => job.status === 'running' || job.status === 'queued');
      if (activeJobs.length > 0) {
        setActiveStatus(`${activeJobs.length} active job${activeJobs.length > 1 ? 's' : ''}`);
      } else {
        setActiveStatus('No active jobs');
      }
    } else {
      setLatestJobDate(null);
      setActiveStatus('No active jobs');
    }
  };

  const groupJobs = () => {
    // Group jobs by base_job_id
    const groups: { [key: string]: JobGroup } = {};
    
    filteredJobs.forEach(job => {
      const baseId = job.base_job_id || job.id;
      
      if (!groups[baseId]) {
        // Find the generation 1 job to get the base name
        const baseJob = filteredJobs.find(j => 
          (j.base_job_id === baseId || j.id === baseId) && j.generation === 1
        );
        
        groups[baseId] = {
          baseJobId: baseId,
          baseJobName: baseJob?.name || job.name,
          generations: []
        };
      }
      
      groups[baseId].generations.push(job);
    });
    
    // Sort generations within each group and convert to array
    const groupArray = Object.values(groups).map(group => ({
      ...group,
      generations: group.generations.sort((a, b) => a.generation - b.generation)
    }));
    
    // Sort groups by latest activity
    groupArray.sort((a, b) => {
      const latestA = Math.max(...a.generations.map(j => new Date(j.created_at).getTime()));
      const latestB = Math.max(...b.generations.map(j => new Date(j.created_at).getTime()));
      return latestB - latestA;
    });
    
    setJobGroups(groupArray);
  };

  const clearProjectFilter = () => {
    setProjectFilterActive(false);
    sessionStorage.removeItem('currentProjectId');
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'running':
        return <Clock className="h-5 w-5 text-blue-600 animate-spin" />;
      case 'failed':
        return <AlertCircle className="h-5 w-5 text-red-600" />;
      default:
        return <Clock className="h-5 w-5 text-gray-400" />;
    }
  };

  const getWorkflowIcon = (name: string) => {
    const lowercaseName = name.toLowerCase();
    if (lowercaseName.includes('enzyme')) return <Beaker className="h-4 w-4 text-blue-600" />;
    if (lowercaseName.includes('antibody')) return <Dna className="h-4 w-4 text-teal-600" />;
    if (lowercaseName.includes('peptide')) return <TestTube className="h-4 w-4 text-amber-600" />;
    return <FileText className="h-4 w-4 text-gray-600" />;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadgeClasses = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'running':
        return 'bg-blue-100 text-blue-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full bg-white">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading jobs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-white">
      <div className="px-6 py-8">
        <div className="w-full">
      {/* Main Container */}
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-3xl font-light text-gray-900">Generations</h1>
              <p className="text-gray-600 mt-1">View and manage all your protein design generations</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-500 italic">
                To create a new generation, navigate to the workflows tab on the lefthand sidebar
              </p>
            </div>
          </div>
        </div>

        {/* Search Bar - directly above table */}
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search generations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

      {/* Generations Table */}
      {filteredJobs.length === 0 ? (
        <div className="bg-white rounded-sm border border-gray-200 p-12 text-center">
          <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-600 mb-4">No generations found matching your criteria</p>
          {searchQuery && (
            <button
              onClick={() => {
                setSearchQuery('');
              }}
              className="text-blue-600 hover:text-blue-700"
            >
              Clear search
            </button>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-sm border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="text-left px-4 py-2.5 text-sm font-normal text-gray-900">Name</th>
                <th className="text-left px-4 py-2.5 text-sm font-normal text-gray-900">Project</th>
                <th className="text-center px-4 py-2.5 text-sm font-normal text-gray-900">Status</th>
                <th className="text-center px-4 py-2.5 text-sm font-normal text-gray-900">Generation</th>
                <th className="text-left px-4 py-2.5 text-sm font-normal text-gray-900">Last Updated</th>
                <th className="text-center px-4 py-2.5 text-sm font-normal text-gray-900">Type</th>
              </tr>
            </thead>
            <tbody>
              {filteredJobs.slice(0, 10).map((job, index) => {
                const projectName = job.project_id ? 
                  (projects.find(p => p.id === job.project_id)?.name || 'Unknown Project') : 
                  'No Project';

                return (
                  <tr 
                    key={job.id}
                    className={`border-b border-gray-100 hover:bg-gray-50 transition-colors cursor-pointer ${
                      index === filteredJobs.slice(0, 10).length - 1 ? 'border-b-0' : ''
                    }`}
                    onClick={() => {
                      if (isInTabContext) {
                        openGenerationTab(job.id);
                      }
                    }}
                  >
                    <td className="px-4 py-2">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 bg-gray-100 rounded-sm flex items-center justify-center flex-shrink-0">
                          <Beaker className="h-3 w-3 text-gray-600" />
                        </div>
                        <span className="font-normal text-gray-900 text-sm truncate max-w-xs">
                          {job.name}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-2">
                      <span className="text-sm text-gray-600 truncate max-w-xs">
                        {projectName}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-center">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-sm text-xs font-normal ${
                        job.status === 'completed' ? 'bg-green-100 text-green-800' :
                        job.status === 'failed' ? 'bg-red-100 text-red-800' :
                        job.status === 'running' ? 'bg-blue-100 text-blue-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {job.status}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-center">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-sm text-xs font-normal bg-gray-100 text-gray-800">
                        Gen {job.generation}
                      </span>
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex items-center gap-1.5 text-sm text-gray-500">
                        <Clock className="h-3 w-3" />
                        <span className="text-sm">{formatDate(job.created_at)}</span>
                      </div>
                    </td>
                    <td className="px-4 py-2 text-center">
                      <div className="flex items-center justify-center gap-1">
                        {getWorkflowIcon(job.name)}
                        <span className="text-xs text-gray-600 capitalize">
                          {job.workflow_type || 'Enzyme'}
                        </span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          
          {/* Show message if there are more than 10 iterations */}
          {filteredJobs.length > 10 && (
            <div className="px-4 py-2 bg-gray-50 border-t border-gray-200 text-center">
              <p className="text-sm text-gray-600">
                Showing 10 of {filteredJobs.length} iterations
              </p>
            </div>
          )}
        </div>
      )}
      </div>
        </div>
      </div>
    </div>
  );
}